import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { COMPANY_CONFIG, CORPORATE_DISTINCTIONS } from '../config/site';

export function AboutPage() {
  return (
    <>
      <SEOHead
        title="About Safaza Technologies, Inc. | Corporate Profile & Engineering Focus"
        description="Learn about Safaza Technologies, Inc., a US-incorporated software development and SaaS company dedicated to building enduring, practical technology."
      />

      {/* Header Banner */}
      <section className="pt-16 pb-16 sm:pt-24 sm:pb-20 bg-white border-b border-[#E4E7EC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0D66E4]/8 border border-[#0D66E4]/20 text-[11px] font-mono font-medium text-[#0D66E4] mb-4">
              <span>ABOUT THE COMPANY</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-[#101828] mb-6 leading-[1.15]">
              Engineering reliable software for modern businesses
              <span className="text-[#0D66E4]">.</span>
            </h1>
            <p className="text-lg text-[#667085] leading-relaxed">
              Safaza Technologies, Inc. is a software development company incorporated in the United
              States. We build custom software applications for commercial clients and develop proprietary
              SaaS products designed to solve practical business challenges.
            </p>
          </div>
        </div>
      </section>

      {/* Core Narrative / Introduction */}
      <section className="py-20 sm:py-24 bg-[#F6F8FB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-6 text-[#475467] leading-relaxed text-base sm:text-lg">
            <p>
              We founded Safaza Technologies with a straightforward mission: to build software that is
              dependable, well-architected, and grounded in real commercial utility. We design software
              systems that streamline workflows, connect distributed tools, and help businesses operate
              with greater clarity.
            </p>
            <p>
              Our founding team brings hands-on experience designing, building, and operating software
              platforms across several industries, including financial technology. That operating
              background informs our technical discipline: we understand how to design resilient data models,
              enforce type safety, handle transaction concurrency, and maintain rigorous security boundaries.
            </p>
            <p>
              While this technical background enables us to build high-performance software for fintech
              and other specialized use cases, Safaza Technologies, Inc. is strictly a software engineering
              firm—not a financial institution, bank, payment processor, or money transmitter.
            </p>
          </div>
        </div>
      </section>

      {/* Structured Detailed Sections */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16">
            
            {/* Who we are */}
            <div className="p-8 sm:p-10 rounded-2xl border border-[#E4E7EC] bg-white shadow-xs">
              <div className="text-xs font-mono font-bold text-[#0D66E4] uppercase mb-2">01 / IDENTITY</div>
              <h2 className="text-2xl font-bold tracking-tight text-[#101828] mb-4">
                Who we are
              </h2>
              <p className="text-sm sm:text-base text-[#667085] leading-relaxed">
                Safaza Technologies, Inc. is an independent software development company incorporated in
                the United States. We approach software engineering as an exacting discipline, valuing
                clean codebases, clear domain boundaries, automated testing, and long-term architectural
                maintainability over quick shortcuts.
              </p>
            </div>

            {/* What we do */}
            <div className="p-8 sm:p-10 rounded-2xl border border-[#E4E7EC] bg-white shadow-xs">
              <div className="text-xs font-mono font-bold text-[#0D66E4] uppercase mb-2">02 / CAPABILITIES</div>
              <h2 className="text-2xl font-bold tracking-tight text-[#101828] mb-4">
                What we do
              </h2>
              <p className="text-sm sm:text-base text-[#667085] leading-relaxed">
                We operate across two complementary business lines: custom software engineering for clients
                (web and mobile applications, API integrations, and workflow automation) and proprietary
                SaaS product development (including the software architecture and engineering for the Akoole
                business management platform).
              </p>
            </div>

            {/* Technical Domain Expertise */}
            <div className="p-8 sm:p-10 rounded-2xl border border-[#E4E7EC] bg-white shadow-xs">
              <div className="text-xs font-mono font-bold text-[#0D66E4] uppercase mb-2">03 / EXPERTISE</div>
              <h2 className="text-2xl font-bold tracking-tight text-[#101828] mb-4">
                Technical domain expertise
              </h2>
              <p className="text-sm sm:text-base text-[#667085] leading-relaxed">
                Our team has deep practical knowledge building software for financial technology workflows.
                We engineer payment gateway connectors (e.g. Stripe, Wise, Paystack), double-entry ledgers,
                and reporting dashboards for clients. This expertise describes our technical capabilities as
                software developers, not a financial services offering.
              </p>
            </div>

            {/* How we engage */}
            <div className="p-8 sm:p-10 rounded-2xl border border-[#E4E7EC] bg-white shadow-xs">
              <div className="text-xs font-mono font-bold text-[#0D66E4] uppercase mb-2">04 / ENGAGEMENT</div>
              <h2 className="text-2xl font-bold tracking-tight text-[#101828] mb-4">
                How we engage &amp; earn revenue
              </h2>
              <p className="text-sm sm:text-base text-[#667085] leading-relaxed">
                We engage with business clients under formal commercial contracts, earning revenue through
                scoped software development milestones, dedicated engineering team retainers, and SaaS
                platform subscriptions. All commercial proceeds are received for bona fide software
                engineering services.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Corporate Structure & Affiliate Relationships */}
      <section className="py-20 sm:py-28 bg-[#F6F8FB] border-t border-[#E4E7EC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto mb-16 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#101828] mb-4">
              Corporate Structure &amp; Entity Relationships
            </h2>
            <p className="text-sm text-[#667085]">
              To maintain complete transparency for clients, commercial partners, and financial
              institutions, we clearly document our corporate boundaries and affiliate relationships.
            </p>
          </div>

          {/* Affiliates Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
            {CORPORATE_DISTINCTIONS.affiliates.map((affiliate, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl p-6 sm:p-8 border border-[#E4E7EC] shadow-xs flex flex-col justify-between"
              >
                <div>
                  <span className="text-[11px] font-mono font-semibold uppercase tracking-wider text-[#0D66E4] block mb-2">
                    {affiliate.jurisdiction}
                  </span>
                  <h3 className="text-lg font-bold text-[#101828] mb-3">
                    {affiliate.name}
                  </h3>
                  <p className="text-xs sm:text-sm text-[#667085] leading-relaxed">
                    {affiliate.role}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Governance Notice Box */}
          <div className="max-w-4xl mx-auto p-6 rounded-2xl bg-white border border-[#E4E7EC] text-xs sm:text-sm text-[#667085] leading-relaxed space-y-3 shadow-xs">
            <h4 className="font-bold text-[#101828] text-sm">
              Operational &amp; Legal Independence
            </h4>
            <p>
              {CORPORATE_DISTINCTIONS.governanceNotice}
            </p>
            <p>
              {CORPORATE_DISTINCTIONS.usEntity.scopeNotice}
            </p>
          </div>

          {/* Corporate Details Table */}
          <div className="max-w-3xl mx-auto mt-12 bg-white rounded-2xl border border-[#E4E7EC] overflow-hidden shadow-xs">
            <div className="divide-y divide-[#E4E7EC]">
              <div className="px-6 py-4 sm:grid sm:grid-cols-3 sm:gap-4 items-center">
                <dt className="text-xs font-semibold uppercase tracking-wider text-[#667085]">
                  Legal Name
                </dt>
                <dd className="mt-1 sm:mt-0 sm:col-span-2 text-sm font-bold text-[#101828]">
                  {COMPANY_CONFIG.legalName}
                </dd>
              </div>

              <div className="px-6 py-4 sm:grid sm:grid-cols-3 sm:gap-4 items-center">
                <dt className="text-xs font-semibold uppercase tracking-wider text-[#667085]">
                  Jurisdiction
                </dt>
                <dd className="mt-1 sm:mt-0 sm:col-span-2 text-sm text-[#101828]">
                  {COMPANY_CONFIG.incorporationCountry}
                </dd>
              </div>

              <div className="px-6 py-4 sm:grid sm:grid-cols-3 sm:gap-4 items-center">
                <dt className="text-xs font-semibold uppercase tracking-wider text-[#667085]">
                  Primary Business
                </dt>
                <dd className="mt-1 sm:mt-0 sm:col-span-2 text-sm text-[#101828]">
                  {COMPANY_CONFIG.businessType}
                </dd>
              </div>

              <div className="px-6 py-4 sm:grid sm:grid-cols-3 sm:gap-4 items-center">
                <dt className="text-xs font-semibold uppercase tracking-wider text-[#667085]">
                  Primary Domain
                </dt>
                <dd className="mt-1 sm:mt-0 sm:col-span-2 text-sm font-mono text-[#0D66E4]">
                  {COMPANY_CONFIG.primaryDomain}
                </dd>
              </div>

              <div className="px-6 py-4 sm:grid sm:grid-cols-3 sm:gap-4 items-center">
                <dt className="text-xs font-semibold uppercase tracking-wider text-[#667085]">
                  Corporate Email
                </dt>
                <dd className="mt-1 sm:mt-0 sm:col-span-2 text-sm text-[#101828]">
                  <a
                    href={`mailto:${COMPANY_CONFIG.contactEmail}`}
                    className="text-[#0D66E4] hover:underline"
                  >
                    {COMPANY_CONFIG.contactEmail}
                  </a>
                </dd>
              </div>
            </div>
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-[#101828] hover:bg-[#0D66E4] rounded-full transition-colors"
            >
              <span>Submit a corporate inquiry</span>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </Link>
          </div>

        </div>
      </section>
    </>
  );
}

